import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Edit, Trash2, Eye } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  faqCategoriesAPI,
  faqQuestionsAPI,
  type FAQCategory,
  type FAQCategoryCreatePayload,
  type FAQQuestion,
  type FAQQuestionCreatePayload,
  PaginationParams,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";

export default function FAQManagement() {
  const [categories, setCategories] = useState<FAQCategory[]>([]);
  const [questions, setQuestions] = useState<FAQQuestion[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  // Pagination state for Categories
  const [catCurrentPage, setCatCurrentPage] = useState(1);
  const [catPageSize, setCatPageSize] = useState(10);
  const [catTotalItems, setCatTotalItems] = useState(0);
  const [catTotalPages, setCatTotalPages] = useState(0);

  // Pagination state for Questions
  const [qCurrentPage, setQCurrentPage] = useState(1);
  const [qPageSize, setQPageSize] = useState(10);
  const [qTotalItems, setQTotalItems] = useState(0);
  const [qTotalPages, setQTotalPages] = useState(0);

  // FAQ Category state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState<FAQCategoryCreatePayload>({
    title: "",
    is_enabled: true,
    order_by: 0,
  });
  const [categorySubmitLoading, setCategorySubmitLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FAQCategory | null>(
    null
  );
  const [deletingCategory, setDeletingCategory] = useState<FAQCategory | null>(
    null
  );
  const [deleteCategoryLoading, setDeleteCategoryLoading] = useState(false);

  // FAQ Question state
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [questionForm, setQuestionForm] = useState<FAQQuestionCreatePayload>({
    category: "",
    question: "",
    answer: "",
    is_enabled: true,
    order_by: 0,
  });
  const [questionSubmitLoading, setQuestionSubmitLoading] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [viewingQuestion, setViewingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [deletingQuestion, setDeletingQuestion] = useState<FAQQuestion | null>(
    null
  );
  const [deleteQuestionLoading, setDeleteQuestionLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const params: PaginationParams = {
        page: catCurrentPage,
        page_size: catPageSize,
      };
      const res = await faqCategoriesAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setCategories(Array.isArray(list) ? list : []);
        const count =
          res.count ??
          (res as any).total_count ??
          (Array.isArray(list) ? list.length : 0);
        setCatTotalItems(count);
        setCatTotalPages(Math.ceil(count / catPageSize));
      }
    } catch {
      toast.error("Failed to load FAQ categories");
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [catCurrentPage, catPageSize]);

  const fetchQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const params: PaginationParams = {
        page: qCurrentPage,
        page_size: qPageSize,
      };
      const res = await faqQuestionsAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setQuestions(Array.isArray(list) ? list : []);
        const count =
          res.count ??
          (res as any).total_count ??
          (Array.isArray(list) ? list.length : 0);
        setQTotalItems(count);
        setQTotalPages(Math.ceil(count / qPageSize));
      }
    } catch {
      toast.error("Failed to load FAQ questions");
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, [qCurrentPage, qPageSize]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const resetCategoryForm = () => {
    setCategoryForm({ title: "", is_enabled: true, order_by: 0 });
    setEditingCategory(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({
      category: categories[0]?.id?.toString() ?? "",
      question: "",
      answer: "",
      is_enabled: true,
      order_by: 0,
    });
    setEditingQuestion(null);
  };

  // Category Handlers
  const handleOpenCreateCategory = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const handleOpenEditCategory = (cat: FAQCategory) => {
    setEditingCategory(cat);
    setCategoryForm({
      title: cat.title,
      is_enabled: cat.is_enabled,
      order_by: cat.order_by,
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.title.trim()) {
      toast.error("Category title is required");
      return;
    }
    setCategorySubmitLoading(true);
    try {
      if (editingCategory) {
        await faqCategoriesAPI.update(editingCategory.id, categoryForm);
        toast.success("Category updated successfully");
      } else {
        await faqCategoriesAPI.create(categoryForm);
        toast.success("Category created successfully");
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save category");
    } finally {
      setCategorySubmitLoading(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deletingCategory) {
      return;
    }
    setDeleteCategoryLoading(true);
    try {
      await faqCategoriesAPI.delete(deletingCategory.id);
      toast.success("Category deleted successfully");
      setDeletingCategory(null);
      fetchCategories();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete category");
    } finally {
      setDeleteCategoryLoading(false);
    }
  };

  // Question Handlers
  const handleOpenCreateQuestion = () => {
    resetQuestionForm();
    setQuestionDialogOpen(true);
  };

  const handleOpenEditQuestion = (q: FAQQuestion) => {
    setEditingQuestion(q);
    const catId = typeof q.category === "object" ? q.category.id : q.category;
    setQuestionForm({
      category: String(catId),
      question: q.question,
      answer: q.answer,
      is_enabled: q.is_enabled,
      order_by: q.order_by,
    });
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question.trim()) {
      toast.error("Question is required");
      return;
    }

    // Strip HTML tags to check if answer is effectively empty
    const answerText = questionForm.answer.replace(/<[^>]*>/g, "").trim();
    if (!answerText) {
      toast.error("Answer is required");
      return;
    }

    if (!questionForm.category) {
      toast.error("Please select a category");
      return;
    }
    setQuestionSubmitLoading(true);
    try {
      const payload = {
        ...questionForm,
        category: Number(questionForm.category) || questionForm.category,
      };
      if (editingQuestion) {
        await faqQuestionsAPI.update(editingQuestion.id, payload);
        toast.success("Question updated successfully");
      } else {
        await faqQuestionsAPI.create(payload);
        toast.success("Question created successfully");
      }
      setQuestionDialogOpen(false);
      resetQuestionForm();
      fetchQuestions();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save question");
    } finally {
      setQuestionSubmitLoading(false);
    }
  };

  const handleDeleteQuestion = async () => {
    if (!deletingQuestion) {
      return;
    }
    setDeleteQuestionLoading(true);
    try {
      await faqQuestionsAPI.delete(deletingQuestion.id);
      toast.success("Question deleted successfully");
      setDeletingQuestion(null);
      fetchQuestions();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete question");
    } finally {
      setDeleteQuestionLoading(false);
    }
  };

  const getCategoryTitle = useCallback(
    (q: FAQQuestion) => {
      if (q.category_name) {
        return q.category_name;
      }
      const catId = typeof q.category === "object" ? q.category.id : q.category;
      const cat = categories.find(c => String(c.id) === String(catId));
      return cat?.title ?? "—";
    },
    [categories]
  );

  const categoryTableColumns = useMemo<TableColumnDef<FAQCategory>[]>(
    () => [
      {
        id: "order",
        label: "Order",
        width: 72,
        renderCell: cat => cat.order_by,
      },
      {
        id: "title",
        label: "Title",
        width: 200,
        renderCell: cat => <span className="font-medium">{cat.title}</span>,
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: cat => (
          <span
            className={
              cat.is_enabled ? "text-green-600" : "text-muted-foreground"
            }
          >
            {cat.is_enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 120,
        alwaysVisible: true,
        renderCell: cat => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEditCategory(cat)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingCategory(cat)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const questionTableColumns = useMemo<TableColumnDef<FAQQuestion>[]>(
    () => [
      {
        id: "order",
        label: "Order",
        width: 72,
        renderCell: q => q.order_by,
      },
      {
        id: "category",
        label: "Category",
        width: 140,
        renderCell: q => getCategoryTitle(q),
      },
      {
        id: "question",
        label: "Question",
        width: 300,
        renderCell: q => (
          <span className="max-w-[300px] truncate font-medium">
            {q.question}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        renderCell: q => (
          <span
            className={
              q.is_enabled ? "text-green-600" : "text-muted-foreground"
            }
          >
            {q.is_enabled ? "Enabled" : "Disabled"}
          </span>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 140,
        alwaysVisible: true,
        renderCell: q => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setViewingQuestion(q)}
              className="text-blue-600 hover:text-blue-700"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEditQuestion(q)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingQuestion(q)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [getCategoryTitle]
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">FAQ Management</h1>
        <p className="text-muted-foreground">
          Manage frequently asked questions and categories
        </p>
      </div>

      <Tabs defaultValue="categories" className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="categories">FAQ Categories</TabsTrigger>
          <TabsTrigger value="questions">FAQ Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="space-y-4">
          <DataTableProvider
            tableId="super-admin-faq-categories"
            columns={categoryTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Categories ({catTotalItems})</CardTitle>
                <div className="flex items-center gap-2">
                  <Button onClick={handleOpenCreateCategory} className="gap-2">
                    <Plus className="h-4 w-4" />
                    Create new category
                  </Button>
                  <DataTableColumnSettings columns={categoryTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {categoriesLoading ? (
                  <div className="flex justify-center py-12">
                    <CustomLoader size="md" />
                  </div>
                ) : categories.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    No categories yet.
                  </p>
                ) : (
                  <DataTable
                    embedded
                    hideToolbar
                    tableId="super-admin-faq-categories"
                    columns={categoryTableColumns}
                    data={categories}
                    getRowKey={cat => cat.id}
                  />
                )}
              </CardContent>
              {catTotalItems > 0 && (
                <div className="border-t p-4">
                  <CommonPagination
                    currentPage={catCurrentPage}
                    totalPages={catTotalPages}
                    pageSize={catPageSize}
                    totalCount={catTotalItems}
                    onPageChange={setCatCurrentPage}
                    onPageSizeChange={size => {
                      setCatPageSize(size);
                      setCatCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </Card>
          </DataTableProvider>
        </TabsContent>

        <TabsContent value="questions" className="space-y-4">
          <DataTableProvider
            tableId="super-admin-faq-questions"
            columns={questionTableColumns}
          >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                <CardTitle>Questions ({qTotalItems})</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleOpenCreateQuestion}
                    className="gap-2"
                    disabled={categories.length === 0}
                  >
                    <Plus className="h-4 w-4" />
                    Create new question
                  </Button>
                  <DataTableColumnSettings columns={questionTableColumns} />
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                {questionsLoading ? (
                  <div className="flex justify-center py-12">
                    <CustomLoader size="md" />
                  </div>
                ) : questions.length === 0 ? (
                  <p className="py-8 text-center text-muted-foreground">
                    No questions yet.
                  </p>
                ) : (
                  <DataTable
                    embedded
                    hideToolbar
                    tableId="super-admin-faq-questions"
                    columns={questionTableColumns}
                    data={questions}
                    getRowKey={q => q.id}
                  />
                )}
              </CardContent>
              {qTotalItems > 0 && (
                <div className="border-t p-4">
                  <CommonPagination
                    currentPage={qCurrentPage}
                    totalPages={qTotalPages}
                    pageSize={qPageSize}
                    totalCount={qTotalItems}
                    onPageChange={setQCurrentPage}
                    onPageSizeChange={size => {
                      setQPageSize(size);
                      setQCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </Card>
          </DataTableProvider>
        </TabsContent>
      </Tabs>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory
                ? "Edit FAQ Category"
                : "Create New FAQ Category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="cat-title">Title *</Label>
              <Input
                id="cat-title"
                value={categoryForm.title}
                onChange={e =>
                  setCategoryForm({ ...categoryForm, title: e.target.value })
                }
                placeholder="Category title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-order">Order</Label>
              <Input
                id="cat-order"
                type="number"
                value={categoryForm.order_by}
                onChange={e =>
                  setCategoryForm({
                    ...categoryForm,
                    order_by: Number(e.target.value),
                  })
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="cat-enabled"
                checked={categoryForm.is_enabled}
                onCheckedChange={checked =>
                  setCategoryForm({ ...categoryForm, is_enabled: checked })
                }
              />
              <Label htmlFor="cat-enabled">Enabled</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setCategoryDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                disabled={categorySubmitLoading}
              >
                {categorySubmitLoading ? (
                  <CustomLoader size="xs" />
                ) : editingCategory ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion
                ? "Edit FAQ Question"
                : "Create New FAQ Question"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="q-category">Category *</Label>
              <Select
                value={String(questionForm.category)}
                onValueChange={v =>
                  setQuestionForm({ ...questionForm, category: v })
                }
              >
                <SelectTrigger id="q-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-text">Question *</Label>
              <Input
                id="q-text"
                value={questionForm.question}
                onChange={e =>
                  setQuestionForm({ ...questionForm, question: e.target.value })
                }
                placeholder="Enter question"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="q-answer">Answer *</Label>
              <div className="rounded-md">
                <ReactQuill
                  theme="snow"
                  value={questionForm.answer}
                  onChange={content =>
                    setQuestionForm({ ...questionForm, answer: content })
                  }
                  placeholder="Enter detailed answer here..."
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ["bold", "italic", "underline", "strike", "blockquote"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "clean"],
                    ],
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="q-order">Order</Label>
                <Input
                  id="q-order"
                  type="number"
                  value={questionForm.order_by}
                  onChange={e =>
                    setQuestionForm({
                      ...questionForm,
                      order_by: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-2 pt-8">
                <Switch
                  id="q-enabled"
                  checked={questionForm.is_enabled}
                  onCheckedChange={checked =>
                    setQuestionForm({ ...questionForm, is_enabled: checked })
                  }
                />
                <Label htmlFor="q-enabled">Enabled</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setQuestionDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveQuestion}
                disabled={questionSubmitLoading}
              >
                {questionSubmitLoading ? (
                  <CustomLoader size="xs" />
                ) : editingQuestion ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation */}
      <Dialog
        open={!!deletingCategory}
        onOpenChange={open => !open && setDeletingCategory(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete FAQ Category</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Are you sure you want to delete category &quot;
            <strong>{deletingCategory?.title}</strong>&quot;? This will not
            delete questions in this category but they will lose their category
            association.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeletingCategory(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
              disabled={deleteCategoryLoading}
            >
              {deleteCategoryLoading ? <CustomLoader size="xs" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Question Dialog */}
      <Dialog
        open={!!viewingQuestion}
        onOpenChange={open => !open && setViewingQuestion(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>FAQ Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div className="grid grid-cols-2 gap-4 border-b pb-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Category
                </Label>
                <p className="mt-1 font-medium">
                  {viewingQuestion ? getCategoryTitle(viewingQuestion) : "—"}
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Status
                </Label>
                <div className="mt-1">
                  <span
                    className={
                      viewingQuestion?.is_enabled
                        ? "font-medium text-green-600"
                        : "font-medium text-muted-foreground"
                    }
                  >
                    {viewingQuestion?.is_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Question
              </Label>
              <p className="text-lg font-semibold leading-relaxed">
                {viewingQuestion?.question}
              </p>
            </div>

            <div className="mt-4 space-y-2 border-t pt-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Answer
              </Label>
              <div
                className="prose prose-slate mt-2 max-w-none text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: viewingQuestion?.answer ?? "",
                }}
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button
                variant="outline"
                onClick={() => setViewingQuestion(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Question Confirmation */}
      <Dialog
        open={!!deletingQuestion}
        onOpenChange={open => !open && setDeletingQuestion(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete FAQ Question</DialogTitle>
          </DialogHeader>
          <p className="py-4 text-muted-foreground">
            Are you sure you want to delete this question?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeletingQuestion(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteQuestion}
              disabled={deleteQuestionLoading}
            >
              {deleteQuestionLoading ? <CustomLoader size="xs" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* Rich Text Editor Styles */}
      <style>{`
                .ql-container {
                    border-bottom-left-radius: 0.5rem;
                    border-bottom-right-radius: 0.5rem;
                    background-color: transparent;
                    font-family: inherit;
                    min-height: 150px;
                }
                .ql-toolbar {
                    border-top-left-radius: 0.5rem;
                    border-top-right-radius: 0.5rem;
                    background-color: var(--muted);
                    border-color: var(--input) !important;
                }
                .ql-container.ql-snow {
                    border-color: var(--input) !important;
                }
                .ql-editor {
                    font-size: 0.875rem;
                    line-height: 1.5;
                }
                .ql-editor.ql-blank::before {
                    color: var(--muted-foreground);
                    font-style: normal;
                    font-size: 0.875rem;
                }
            `}</style>
    </div>
  );
}
